import React, { useEffect, useState } from "react";
import {
  FaArrowRight,
  FaExclamationTriangle,
  FaLock,
  FaRedo,
  FaTrashAlt,
} from "react-icons/fa";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  confirmAccountDeletion,
  handleAccountDeletionError,
  verifyDeletionToken,
} from "../api/account-deletion";
import { Button, Field, PasswordInput, Spinner } from "../ui";
import AuthLayout from "../components/site/AuthLayout";

const SUPPORT_EMAIL = "tarbaa.kw@gmail.com";

const AccountDeletionConfirm = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(true);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [tokenValid, setTokenValid] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [error, setError] = useState("");
  // eslint-disable-next-line no-unused-vars
  const [deletionId, setDeletionId] = useState("");
  const [expiresAt, setExpiresAt] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(null);

  const token = searchParams.get("token");
  const email = searchParams.get("email");

  useEffect(() => {
    if (token && email) {
      verifyToken();
    } else {
      setTokenValid(false);
      setError("رابط غير صالح");
      setVerifyLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, email]);

  const verifyToken = async () => {
    try {
      setVerifyLoading(true);
      const response = await verifyDeletionToken(token, email);

      if (response.success) {
        setTokenValid(true);
        setUserInfo(response.user);
        setDeletionId(response.deletionId);
        setExpiresAt(response.expiresAt);
      } else {
        setTokenValid(false);
        setError(response.message || "رمز التحقق غير صالح");
      }
    } catch (err) {
      console.error("Token verification error:", err);
      setTokenValid(false);
      const errorMessage = handleAccountDeletionError(err);
      setError(errorMessage);
    } finally {
      setVerifyLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!password.trim()) {
      toast.error("الرجاء إدخال كلمة المرور");
      return;
    }

    if (password.length < 6) {
      toast.error("كلمة المرور يجب أن تكون 6 أحرف على الأقل");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await confirmAccountDeletion(token, email, password);

      if (response.success) {
        // Clear any stored user data
        localStorage.clear();
        sessionStorage.clear();

        toast.success("تم حذف حسابك بنجاح. نأسف لرؤيتك تغادر");

        // Redirect to goodbye page
        navigate("/account-deleted", {
          state: {
            message: response.message,
            userName: userInfo?.name,
            timestamp: response.timestamp,
          },
        });
      } else {
        setError(response.message || "حدث خطأ أثناء حذف الحساب");
        toast.error(response.message || "حدث خطأ أثناء حذف الحساب");
      }
    } catch (err) {
      console.error("Account deletion error:", err);
      const errorMessage = handleAccountDeletionError(err);
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Update countdown every second
  useEffect(() => {
    if (!expiresAt) return;

    const updateTimer = () => {
      const now = new Date();
      const expiry = new Date(expiresAt);
      const diff = expiry - now;

      if (diff <= 0) {
        setTimeRemaining("منتهي الصلاحية");
        return false; // Signal to stop interval
      }

      const minutes = Math.floor(diff / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      setTimeRemaining(`${minutes}:${seconds.toString().padStart(2, "0")}`);
      return true; // Continue interval
    };

    // Initial calculation
    if (!updateTimer()) return;

    // Set up interval to update every second
    const interval = setInterval(() => {
      if (!updateTimer()) {
        clearInterval(interval);
      }
    }, 1000);

    // Cleanup interval on unmount
    return () => clearInterval(interval);
  }, [expiresAt]);

  const expired = timeRemaining === "منتهي الصلاحية";

  // Loading state
  if (verifyLoading) {
    return (
      <AuthLayout>
        <div
          role="status"
          className="py-8 flex flex-col items-center justify-center gap-4 text-white/80"
        >
          <Spinner size={32} className="text-primary" />
          <p className="text-lg">جارٍ التحقق من رمز الحذف...</p>
        </div>
      </AuthLayout>
    );
  }

  // Invalid token state
  if (tokenValid === false) {
    return (
      <AuthLayout title="رمز غير صالح">
        <div className="text-center">
          <div className="w-16 h-16 rounded-2xl bg-danger/15 text-danger flex items-center justify-center mx-auto mb-5">
            <FaExclamationTriangle className="text-2xl" aria-hidden="true" />
          </div>
          <p className="text-white/80 text-lg leading-relaxed">{error}</p>
        </div>

        <div className="mt-6 space-y-3">
          <Button block to="/account-deletion-request">
            طلب رابط جديد
          </Button>
          <Button block variant="secondary" to="/">
            العودة إلى الصفحة الرئيسية
          </Button>
        </div>
      </AuthLayout>
    );
  }

  // Main confirmation form
  return (
    <AuthLayout
      title="تأكيد حذف الحساب"
      subtitle="هذا الإجراء لا يمكن التراجع عنه"
      footer={
        <div className="space-y-1">
          <p className="font-bold text-white">هل تحتاج مساعدة؟</p>
          <p className="leading-relaxed">
            إذا كنت تواجه مشكلة معينة، ربما نستطيع المساعدة بدلاً من حذف الحساب.
          </p>
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            className="inline-block font-bold text-primary hover:text-mainText underline-offset-4 hover:underline"
          >
            تواصل مع فريق الدعم
          </a>
        </div>
      }
    >
      {userInfo && (
        <div className="text-center mb-6 pb-6 border-b border-white/10">
          <p className="text-white/70">مرحباً</p>
          <p className="font-bold text-lg mt-1">{userInfo.name}</p>
          <p dir="ltr" className="text-sm text-white/55 break-all">
            {userInfo.email}
          </p>

          {/* Timer */}
          {expiresAt && timeRemaining && (
            <div
              className={`mt-3 inline-flex items-center gap-2 px-3 py-1 rounded-full border text-sm font-semibold ${
                expired
                  ? "bg-danger/15 text-danger border-danger/40"
                  : "bg-primary/15 text-primary border-primary/30"
              }`}
            >
              <FaLock className="text-xs shrink-0" aria-hidden="true" />
              <span className="tabular-nums">ينتهي خلال: {timeRemaining}</span>
            </div>
          )}
        </div>
      )}

      {/* Final Warning */}
      <div className="rounded-xl bg-danger/10 border border-danger/40 p-4">
        <p className="font-bold text-danger mb-2 flex items-center gap-2">
          <FaExclamationTriangle className="shrink-0" aria-hidden="true" />
          تحذير أخير
        </p>
        <ul className="list-disc pr-5 space-y-1 text-sm text-white/85">
          <li>
            سيتم حذف حسابك <strong className="text-white">نهائياً</strong> خلال
            دقائق
          </li>
          <li>ستفقد جميع بياناتك: النقاط، الإنجازات، الأصدقاء</li>
          <li>لن تتمكن من استخدام نفس البريد الإلكتروني لمدة 30 يوماً</li>
          <li>
            هذا الإجراء <strong className="text-white">لا يمكن التراجع عنه</strong>
          </li>
        </ul>
      </div>

      {/* Password Form */}
      <form onSubmit={handleSubmit} className="mt-6 space-y-5">
        {error && (
          <div
            role="alert"
            className="rounded-xl bg-danger/10 border border-danger/40 text-danger px-4 py-3 text-sm font-medium text-center"
          >
            {error}
          </div>
        )}

        {expired && (
          <div
            role="alert"
            className="rounded-xl bg-danger/10 border border-danger/40 text-danger px-4 py-3 text-sm font-medium text-center"
          >
            انتهت صلاحية رابط الحذف. يرجى طلب رابط جديد.
          </div>
        )}

        <Field
          label="أدخل كلمة المرور الحالية للتأكيد"
          htmlFor="deletion-password"
          hint="اكتب كلمة المرور الحالية لتأكيد عملية الحذف"
        >
          <PasswordInput
            id="deletion-password"
            autoComplete="current-password"
            shown={showPassword}
            onToggle={() => setShowPassword(!showPassword)}
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="كلمة المرور الحالية"
            disabled={loading}
            minLength={6}
          />
        </Field>

        <div className="space-y-3">
          <Button
            type="submit"
            variant="danger"
            size="lg"
            block
            disabled={loading || !password.trim() || expired}
            icon={loading ? <Spinner /> : <FaTrashAlt aria-hidden="true" />}
          >
            {loading
              ? "جارٍ حذف الحساب..."
              : expired
                ? "انتهت الصلاحية"
                : "تأكيد حذف الحساب نهائياً"}
          </Button>

          {expired ? (
            <Button
              block
              to="/account-deletion-request"
              icon={<FaRedo className="text-sm" aria-hidden="true" />}
            >
              طلب رابط جديد
            </Button>
          ) : null}

          <Button
            block
            variant="secondary"
            to="/"
            icon={<FaArrowRight className="text-xs" aria-hidden="true" />}
          >
            إلغاء والعودة للرئيسية
          </Button>
        </div>
      </form>
    </AuthLayout>
  );
};

export default AccountDeletionConfirm;
